// The progress views' banned-word guard (design §11): no string the One-Pager,
// the Timeline, the centre roster or their PDF can produce may carry assessment
// vocabulary, in either language. Same approach as pdfModel's assertModelClean —
// walk every string value AND key — but with the progress list.
//
// Matching is a case-insensitive substring test, deliberately blunt: a word
// that merely CONTAINS a banned one ("upgrade", Malay "fail" = file) must be
// rephrased rather than allow-listed, because a list of exceptions is where a
// real regression would hide.

export const PROGRESS_BANNED_WORDS = [
  "score",
  "%",
  "percent",
  "grade",
  "mastered",
  "behind",
  "delay",
  "at risk",
  "on track",
  "expected",
  "emerging",
  "percentile",
  "streak",
  "wrong",
  "fail",
] as const;

/** The first banned word found in `text`, or null. */
export function findBannedWord(text: string): string | null {
  const lower = text.toLowerCase();
  for (const w of PROGRESS_BANNED_WORDS) if (lower.includes(w)) return w;
  return null;
}

/** Every string (value or key) reachable from `value`, with its path. */
export function collectStrings(value: unknown, path = "$", out: [string, string][] = []): [string, string][] {
  if (typeof value === "string") {
    out.push([path, value]);
  } else if (Array.isArray(value)) {
    value.forEach((v, i) => collectStrings(v, `${path}[${i}]`, out));
  } else if (value && typeof value === "object") {
    for (const [k, v] of Object.entries(value)) {
      out.push([`${path}.<key>`, k]);
      collectStrings(v, `${path}.${k}`, out);
    }
  }
  return out;
}

/** Throws on the first banned word anywhere in `value`. */
export function assertProgressClean(value: unknown, where = "progress model"): void {
  for (const [path, text] of collectStrings(value)) {
    const hit = findBannedWord(text);
    if (hit) {
      throw new Error(
        `${where} ${path} = "${text}" contains the banned word "${hit}" — progress views ` +
          "describe what was seen during play, never a score, a grade or an expectation.",
      );
    }
  }
}

/**
 * Strip // and /* *\/ comments from TS/TSX source before scanning it, keeping
 * string literals intact (a "//" inside a URL string is not a comment). Source
 * legitimately NAMES what it must not do in its comments (CLAUDE.md §10).
 */
export function stripComments(src: string): string {
  let out = "";
  let i = 0;
  let quote: string | null = null;
  while (i < src.length) {
    const c = src[i];
    const n = src[i + 1];
    if (quote) {
      out += c;
      if (c === "\\") {
        out += n ?? "";
        i += 2;
        continue;
      }
      if (c === quote) quote = null;
      i++;
      continue;
    }
    if (c === '"' || c === "'" || c === "`") {
      quote = c;
      out += c;
      i++;
      continue;
    }
    if (c === "/" && n === "/") {
      while (i < src.length && src[i] !== "\n") i++;
      continue;
    }
    if (c === "/" && n === "*") {
      i += 2;
      while (i < src.length && !(src[i] === "*" && src[i + 1] === "/")) i++;
      i += 2;
      continue;
    }
    out += c;
    i++;
  }
  return out;
}
